<template>
  <div
    v-if="games && game"
    class="text-center"
  >
    <header class="page-game__header">
      <div
        class="page-game__cta text-h6 text-uppercase text-bold text-primary q-py-md"
      >
        <p>
          <template v-if="game.status === GAME_STATUSES.COMPLETED">
            {{ $t('statuses.game_completed') }}
          </template>
          <template v-else>
            {{ $t('titles.choose_duel') }}:
          </template>
        </p>
      </div>
      <h1 class="page-game__title text-body2 q-px-sm">
        <q-item
          :class="{ [getGameColor(game.status)]: true }"
          class="text-center shadow-2 rounded-borders"
        >
          <q-item-section avatar>
            <q-img
              :src="getLogoUrl(game.awayTeam.abbreviation)"
              :ratio="1"
              :alt="game.awayTeam.abbreviation"
              :contain="true"
            />
          </q-item-section>
          <q-item-section>
            <q-item class="q-pa-none q-pb-sm">
              <q-item-section class="text-right">
                <q-item-label
                  overline
                  class="text-white"
                >
                  <p>{{ game.awayTeam.city }}</p>
                  <p class="text-bold">
                    {{ game.awayTeam.name }}
                  </p>
                </q-item-label>
              </q-item-section>
              <q-item-section class="col-1 text-white text-bold">
                {{ $t('@') }}
              </q-item-section>
              <q-item-section class="text-left">
                <q-item-label
                  overline
                  class="text-white"
                >
                  <p>{{ game.homeTeam.city }}</p>
                  <p class="text-bold">
                    {{ game.homeTeam.name }}
                  </p>
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item-label
              caption
              class="text-bold text-white"
            >
              <p>{{ gameDate }}</p>
              <p>{{ game.location }}</p>
            </q-item-label>
          </q-item-section>
          <q-item-section avatar>
            <q-img
              :src="getLogoUrl(game.homeTeam.abbreviation)"
              :ratio="1"
              :alt="game.awayTeam.abbreviation"
              :contain="true"
            />
          </q-item-section>
        </q-item>
      </h1>
    </header>
    <div class="page-game__content q-py-md relative-position">
      <template v-if="duels">
        <template v-if="hasDuels">
          <div
            v-for="(duelsByInning, duelInnings, $index) in duelsByInnings"
            :class="{ 'q-mt-md': $index > 0 }"
            class="q-mx-lg"
          >
            <h3 class="text-h6 text-green-10">
              {{ $t('titles.innings_qty', [duelInnings]) }}
            </h3>
            <list-duels :data="duelsByInning" />
          </div>
          <div
            v-if="
              game.status === GAME_STATUSES.LIVE &&
                duels.length === 1 &&
                duels[0].isFilteredByUser
            "
            class="q-py-sm"
          >
            <em>{{ $t('messages.already_participated') }}</em>
          </div>
        </template>
        <div
          v-else-if="game.status !== GAME_STATUSES.COMPLETED"
          class="text-h5"
        >
          {{ $t('titles.no_duels') }}
        </div>
      </template>
      <div
        v-else
        class="text-h5"
      >
        {{ $t('titles.wait') }}
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { mapGetters } from 'vuex';
import store from '@/store';
import edtDate from '@/helpers/edt-date';
import { GAME_STATUSES } from '@/helpers';
import { DUEL_STATUSES } from '@/helpers/constants';

import ListDuels from '@/components/common/ListDuels';

@Component({
  name: 'page-game',
  components: {
    ListDuels,
  },
  computed: mapGetters(['games']),
  beforeRouteEnter(to, from, next) {
    const { id } = to.params;

    const activeGameId = store.getters.activeGameId;
    const activeDuelId = store.getters.activeDuelId;

    if (activeDuelId && +activeGameId === +id) {
      next({
        name: 'duel',
        params: {
          id,
          duelId: activeDuelId,
        },
      });
    } else {
      next();
    }
  },
})
export default class PageGame extends Vue {
  // !DATA
  GAME_STATUSES = GAME_STATUSES;
  game = null;
  duels = null;

  get id() {
    return +this.$route.params.id;
  }
  get gameExists() {
    return !!this.game;
  }
  get gameDate() {
    return edtDate(this.game.startDate);
  }
  get hasDuels() {
    return this.duels && this.duels.length > 0;
  }
  get duelInningsTypes() {
    return Array.from(new Set(this.duels.map(({ innings }) => innings))).sort();
  }
  get duelsByInnings() {
    const result = {};
    this.duels.forEach(duel => {
      if (!result[duel.innings]) result[duel.innings] = [];
      result[duel.innings].push(duel);
    });
    return result;
  }

  // !METHODS
  getGameColor(status) {
    switch (status) {
      case GAME_STATUSES.SCHEDULED:
        return 'bg-secondary';
      case GAME_STATUSES.LIVE:
        return 'bg-primary';
      case GAME_STATUSES.COMPLETED:
        return 'bg-grey';
    }
  }
  getEdtDate(date, template) {
    return edtDate(date, template);
  }
  getLogoUrl(name) {
    return `${this.$appConfig.bucket_url}/${name.toLowerCase()}.svg`;
  }
  onDuelsUpdated({ game, duels }) {
    if (+game.id === +this.$route.params.id) {
      this.duels = duels.slice();
      this.$q.notify({
        message: this.$t('messages.duels_updated'),
        color: 'primary',
      });
    }
  }

  beforeDestroy() {
    this.$eventHub.$off('duels-update');
  }
  async created() {
    this.$eventHub.$on('duels-update', this.onDuelsUpdated);

    if (this.games) {
      this.game = this.games.find(({ id }) => +id === +this.id) || null;
    }

    if (!this.game) {
      this.$q.loading.show();
      try {
        this.game = await this.$api.Game.getGame(this.id);
        if (edtDate(this.game.startDate, 'D') !== edtDate(new Date(), 'D')) {
          this.$q.notify({
            color: 'warning',
            message: this.$t('messages.game_outdated'),
            classes: 'text-center',
          });
          this.$router.replace({
            name: 'games',
          });
        }
      } catch (err) {
        this.$q.notify({
          color: 'warning',
          message: this.$t('messages.game_outdated'),
          classes: 'text-center text-black',
        });
        this.$router.replace({
          name: 'games',
        });
      }
      this.$q.loading.hide();
    }

    if (!this.game) return;

    try {
      let duels = await this.$api.Duel.getDuels(this.id, true);
      duels.forEach(duel => {
        duel.isFilteredByUser = true;
      });
      if (duels.length === 0 && this.game.status !== GAME_STATUSES.COMPLETED) {
        duels = await this.$api.Duel.getDuels(this.id, false);
      }
      this.duels = duels;
    } catch (err) {
      console.error(err);
    }
    this.$q.loading.hide();
  }
}
</script>
<style lang="styl">
@import '~quasar-variables';
.page-game {
  &__cta {
    padding-right: 5px + 42px + 5px + 15px;
    padding-left: 5px + 42px + 5px + 15px;
  }
  &__subtitle {
    p {
      margin: 0;
    }
  }
}
</style>
<i18n>
{
  "en": {
    "statuses": {
      "game_completed": "Game is completed"
    },
    "titles": {
      "choose_duel": "Choose your duel for",
      "innings_qty": "{0} innings",
      "no_duels": "No duels created yet!",
      "wait": "Please, wait..."
    },
    "messages": {
      "cant_join": "You can't join the game already passed. Please, choose another one.",
      "game_outdated": "This game is outdated",
      "duels_updated": "Duels updated",
      "already_participated": "You can't join other duels after your active one is finished."
    }
  }
}
</i18n>
